/* eslint-disable jsx-a11y/label-has-associated-control */
import { FC, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import moment, { Moment } from 'moment';
import { RadioGroup, useFormApi, useFormState } from 'informed';
import { Dropdown, DateField } from '@ui-modules/informed';
import { MultiSteps } from '@common/interfaces';
import { notEmptyValidator } from '@common/utils';
import css from './styles.module.scss';
import rootCSS from '../../styles.module.scss';

const DRIVE_DATE_FORMAT = 'DD MMMM YYYY';

export interface IRecurringDrive {
  currentStep: string;
  currentDate?: string;
  multiLeg?: boolean;
}

const getWeeklyRecurringDrives = (currentDate: Moment, untilDate: Moment): Moment[] => {
  const weekyDrives = [];
  const iterationDate = currentDate.clone();

  while (iterationDate.isSameOrBefore(untilDate)) {
    weekyDrives.push(iterationDate.clone());
    iterationDate.add(7, 'days');
  }

  return weekyDrives;
};

const RecurringDrive: FC<IRecurringDrive> = ({
  currentDate = new Date(),
  currentStep,
  multiLeg = false,
}) => {
  const formApi = useFormApi();
  const { values } = useFormState() as Record<string, any>;
  const { t, i18n } = useTranslation();

  const recurringPeriod = values?.[currentStep]?.recurring_period;
  const recurringUntil = values?.[currentStep]?.recurring_until;
  const recurringFrequency = values?.[currentStep]?.recurring_frequency;

  const pickupDate = multiLeg
    ? values[MultiSteps.TripInfo]?.drives?.[0].pickup_date
    : values[MultiSteps.TripInfo]?.pickup_date;

  useEffect(() => {
    if (recurringUntil && pickupDate && recurringUntil < pickupDate) {
      formApi.resetField(`${currentStep}.recurring_until`);
    }
  }, [pickupDate, recurringUntil, formApi, currentStep]);

  const recurringOptions = useMemo(
    () => [
      {
        label: t('common.daily'),
        value: '3',
      },
      {
        label: t('common.weekly'),
        value: '2',
      },
    ],
    [t],
  );

  const dailyFrequencyOptions = useMemo(
    () => [
      {
        label: t('common.recurring.workingDaysFromMonday'),
        value: '5',
      },
      {
        label: t('common.recurring.workingDaysFromSunday'),
        value: '6',
      },
      {
        label: t('common.recurring.allDays'),
        value: '7',
      },
    ],
    [t],
  );

  return (
    <div className="add-recurring-section">
      <h3 className={rootCSS.steptitle}>{t('common.recurring.addRecurring')}</h3>
      <div className={rootCSS.wrap}>
        <div className={rootCSS.column}>
          <div className={rootCSS.field}>
            <Dropdown
              className={cn('dark-border', 'small-list')}
              labelClassName={rootCSS['dropdown-label']}
              validate={notEmptyValidator}
              name="recurring_period"
              label={t('common.recurring.repeatDrive')}
              options={recurringOptions}
            />
          </div>
        </div>
        <div className={rootCSS.column}>
          <div className={rootCSS.field}>
            <DateField
              name="recurring_until"
              className={rootCSS.input}
              validate={notEmptyValidator}
              placeholder="Please select date"
              label={t('common.recurring.repeatUntil')}
              minDate={moment(pickupDate || currentDate)
                .add(1, 'days')
                .toDate()}
              maxDate={moment(pickupDate || currentDate)
                .add(29, 'days')
                .toDate()}
              showMonthDropdown
              showYearDropdown
              dropdownMode="select"
            />
          </div>
        </div>
      </div>
      {recurringPeriod?.value === '3' ? (
        <div className={css['recurring-frequency']}>
          <label className="hbh-date-label">{t('common.recurring.dailyFrequency')}</label>
          <RadioGroup
            defaultValue={dailyFrequencyOptions[0].value}
            name="recurring_frequency"
            options={dailyFrequencyOptions}
          />
        </div>
      ) : null}
      {recurringPeriod && recurringUntil ? (
        <div className={css['creating-recurring-panel']}>
          <h3 className={rootCSS.steptitle}>{t('common.recurring.drivesOverview')}</h3>
          {recurringPeriod?.value === '3' ? (
            <p className={css['recurring-drive']}>
              {t('common.recurring.dailyRecurringDrive')} {t('common.from')}{' '}
              <i>{moment(currentDate).locale(i18n.language).format(DRIVE_DATE_FORMAT)}</i>{' '}
              {t('common.to')}{' '}
              <i>{moment(recurringUntil).locale(i18n.language).format(DRIVE_DATE_FORMAT)}</i>{' '}
              {recurringFrequency === '5' ? `(${t('common.recurring.onlyWorkingDays')})` : ''}
            </p>
          ) : (
            getWeeklyRecurringDrives(moment(currentDate), moment(recurringUntil)).map((date) => (
              <p className={css['recurring-drive']}>
                {t('common.recurring.weeklyRecurringDrive')}{' '}
                <i>{date.locale(i18n.language).format(DRIVE_DATE_FORMAT)}</i>
              </p>
            ))
          )}
        </div>
      ) : null}
    </div>
  );
};

export default RecurringDrive;
